import React, {useRef, useState, useCallback, useEffect} from 'react';
import {Chart, ReactGoogleChartEvent} from 'react-google-charts';
import {IconButton, Slider, Divider, Toolbar, Box, Typography} from '@mui/material';
import {default as MuiAppBar} from '@mui/material/AppBar';
import {ZoomIn, ZoomOut, RestartAlt, Print, Download} from '@mui/icons-material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import {useModal} from 'common/hooks/useModal';
import {useTranslation} from 'react-i18next';
import {FilterTransactionType, IFilterTransactions} from 'types/diplomat/FilterTransactions';
import FilterTransactionDialog from 'features/layout/DialogRoutes/dialogs/transactions/FilterTransactionDialog';
import {colors} from 'common/colors';
import {ReactComponent as Logo} from 'images/cs-logo.svg';
import {useDebouncedState} from 'common/hooks/useDebouncedState';
import {useFetchHipaaData} from './useFetchHipaaData';
import {ThemeToggler} from 'features/layout/AppBar/Menu/ThemeSubMenu/ThemeToggler';

// Define the maximum zoom level as a constant
const DEFAULT_MAX_ZOOM = 10;

const baseOptions = {
   sankey: {
      iterations: 2048,
      node: {
         label: {fontSize: 12},
         width: 15,
      },
      link: {
         colorMode: 'source',
         lineWidth: 1,
      },
   },
   tooltip: {
      // Disable built-in tooltip so we can show our custom one.
      trigger: 'none',
      isHtml: true,
   },
};

const SankeyChart = () => {
   const {t} = useTranslation();
   const chartRef = useRef<HTMLDivElement | null>(null);
   const toolbarRef = useRef<HTMLDivElement | null>(null);
   const svgContainerRef = useRef<HTMLDivElement | null>(null);
   const tooltipRef = useRef<HTMLDivElement | null>(null);
   const {open, handleOpen, handleClose} = useModal(false);
   const [filterData, setFilterData] = useState<IFilterTransactions>();
   const {fetchData, data} = useFetchHipaaData(handleClose);

   const MAX_ZOOM = data?.length && data?.length > 100 ? 30 : DEFAULT_MAX_ZOOM;

   useEffect(() => {
      (!data || data.length <= 1) && handleOpen();
   }, [data, handleOpen]);

   // Fixed canvas dimensions (full viewport)
   const canvasWidth = window.innerWidth;
   const canvasHeight = window.innerHeight - (toolbarRef?.current?.clientHeight || 0) - 10; // - border bottom of header and 5 more for margin

   // Remove canvasOffset state; store offset in a ref and update DOM directly.
   const offsetRef = useRef({left: 0, top: 0});

   // We'll store the current zoom in a ref (for immediate DOM updates)
   // and also maintain a debounced state (for the slider) via our custom hook.
   const zoomRef = useRef<number>(1);
   const [zoom, setDebouncedZoom] = useDebouncedState<number>(1, 100);

   // Update zoom directly in the DOM
   const updateZoomDOM = useCallback((newZoom: number) => {
      zoomRef.current = newZoom;
      if (svgContainerRef.current) {
         svgContainerRef.current.style.transform = `scale(${newZoom})`;
      }
   }, []);

   // Combined zoom update: update DOM immediately and schedule debounced state update
   const updateZoom = useCallback(
      (newZoom: number) => {
         updateZoomDOM(newZoom);
         setDebouncedZoom(newZoom);
      },
      [updateZoomDOM, setDebouncedZoom]
   );

   // Reset zoom and panning by updating DOM directly
   const handleResetZoom = useCallback(() => {
      updateZoom(1);
      offsetRef.current = {left: 0, top: 0};
      if (svgContainerRef.current) {
         svgContainerRef.current.style.left = '0px';
         svgContainerRef.current.style.top = '0px';
      }
   }, [updateZoom]);

   // Handler for zoom changes via slider
   const handleZoomChange = useCallback(
      (_: Event, newValue: number | number[]) => {
         if (typeof newValue === 'number') {
            const newZoom = newValue;
            if (chartRef.current) {
               const parentRect = chartRef.current.getBoundingClientRect();
               const mx = parentRect.width / 2;
               const my = parentRect.height / 2;
               const currentOffset = offsetRef.current;
               const newLeft = mx - (newZoom / zoomRef.current) * (mx - currentOffset.left);
               const newTop = my - (newZoom / zoomRef.current) * (my - currentOffset.top);
               offsetRef.current = {left: newLeft, top: newTop};
               if (svgContainerRef.current) {
                  svgContainerRef.current.style.left = `${newLeft}px`;
                  svgContainerRef.current.style.top = `${newTop}px`;
               }
            }
            updateZoom(newZoom);
         }
      },
      [updateZoom]
   );

   // Wheel event handler for zooming, scaling around the mouse cursor
   const handleWheel = useCallback(
      (event: React.WheelEvent<HTMLDivElement>) => {
         event.preventDefault();
         const delta = event.deltaY < 0 ? 1.1 : 0.9;
         let newZoom = zoomRef.current * delta;
         newZoom = Math.min(Math.max(newZoom, 1), MAX_ZOOM);
         if (chartRef.current) {
            const parentRect = chartRef.current.getBoundingClientRect();
            const mx = event.clientX - parentRect.left;
            const my = event.clientY - parentRect.top;
            const currentOffset = offsetRef.current;
            const newLeft = mx - (newZoom / zoomRef.current) * (mx - currentOffset.left);
            const newTop = my - (newZoom / zoomRef.current) * (my - currentOffset.top);
            offsetRef.current = {left: newLeft, top: newTop};
            if (svgContainerRef.current) {
               svgContainerRef.current.style.left = `${newLeft}px`;
               svgContainerRef.current.style.top = `${newTop}px`;
            }
         }
         updateZoom(newZoom);
      },
      [updateZoom]
   );

   // Mouse down handler for chart panning (dragging) with direct DOM updates.
   const handleMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      let lastX = event.clientX;
      let lastY = event.clientY;

      const handleMouseMove = (e: MouseEvent) => {
         const dx = e.clientX - lastX;
         const dy = e.clientY - lastY;
         lastX = e.clientX;
         lastY = e.clientY;
         const currentOffset = offsetRef.current;
         const newOffset = {left: currentOffset.left + dx, top: currentOffset.top + dy};
         offsetRef.current = newOffset;
         if (svgContainerRef.current) {
            svgContainerRef.current.style.left = `${newOffset.left}px`;
            svgContainerRef.current.style.top = `${newOffset.top}px`;
            svgContainerRef.current.style.cursor = 'grabbing';
         }
      };

      const handleMouseUp = () => {
         if (svgContainerRef.current) {
            svgContainerRef.current.style.cursor = 'grab';
         }
         window.removeEventListener('mousemove', handleMouseMove);
         window.removeEventListener('mouseup', handleMouseUp);
      };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
   }, []);

   // Handler for downloading SVG
   const handleDownloadSvg = useCallback(() => {
      if (!chartRef.current) return;
      const svgElement = chartRef.current.querySelector('svg');
      if (!svgElement) return;
      const serializer = new XMLSerializer();
      const source = serializer.serializeToString(svgElement);
      const blob = new Blob([source], {type: 'image/svg+xml;charset=utf-8'});
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sankey_chart.svg';
      a.click();
   }, []);

   // Chart events to update tooltip content when hovering on a link.
   // Assumes that onmouseover provides eventArgs with a "row" property.
   const chartEvents = [
      {
         eventName: 'ready' as const,
         callback: ({chartWrapper, google}) => {
            const chart = chartWrapper?.getChart();
            if (!chart) {
               console.error('No chart');
               return;
            }
            //@ts-ignore
            google.visualization.events.addListener(chart, 'onmouseover', (e) => {
               const dataTable = chartWrapper?.getDataTable();
               // const row = eventArgs.row;
               //@ts-ignore
               const {row} = e;
               // const {row, column} = e;
               // console.warn('MOUSE OVER ', {row, column});
               if (row != null && dataTable) {
                  const source = dataTable.getValue(row, 0);
                  const target = dataTable.getValue(row, 1);
                  const weight = dataTable.getValue(row, 2);
                  if (tooltipRef.current) {
                     tooltipRef.current.innerHTML = `${source} > ${target} : ${weight}`;
                     tooltipRef.current.style.display = 'block';
                  }
               }
            });
            //@ts-ignore
            google.visualization.events.addListener(chart, 'onmouseout', (e) => {
               // const {row, column} = e;
               // console.warn('MOUSE OUT ', {row, column});
               if (tooltipRef.current) {
                  tooltipRef.current.style.display = 'none';
               }
            });
         },
      } as ReactGoogleChartEvent,
   ];

   // Handler to position the custom tooltip near the mouse pointer.
   const handleMouseMoveForTooltip = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
      if (chartRef.current && tooltipRef.current) {
         const rect = chartRef.current.getBoundingClientRect();
         // Position tooltip 10px offset from the pointer
         const x = event.clientX - rect.left + 10;
         const y = event.clientY - rect.top + 10;
         tooltipRef.current.style.left = `${x}px`;
         tooltipRef.current.style.top = `${y}px`;
      }
   }, []);

   return (
      <div>
         {/* Header Toolbar using MUI AppBar */}
         <MuiAppBar
            color="default"
            elevation={0}
            sx={{
               borderBottom: `5px solid ${colors.headerBorder}`,
            }}
         >
            <Toolbar variant="dense">
               {/* Left side: Logo and Title */}
               <div style={{display: 'flex', alignItems: 'center'}}>
                  <Logo style={{margin: '0 3rem'}} />
               </div>
               {/* Right side: Controls */}
               <div
                  style={{
                     display: 'flex',
                     alignItems: 'center',
                     gap: '12rem',
                     width: '100%',
                     justifyContent: 'end',
                  }}
               >
                  <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                     <IconButton
                        onClick={handleOpen}
                        sx={{marginRight: '2rem'}}
                        title={t('Filter')}
                     >
                        {filterData?._type === FilterTransactionType.enum.SELECTED ? (
                           <FilterAltIcon />
                        ) : (
                           <FilterAltOutlinedIcon />
                        )}
                     </IconButton>
                     <IconButton onClick={handleResetZoom} title={t('Reset zoom and position')}>
                        <RestartAlt />
                     </IconButton>
                     <IconButton onClick={() => updateZoom(Math.max(zoomRef.current / 1.2, 1))}>
                        <ZoomOut />
                     </IconButton>
                     <Slider
                        orientation="horizontal"
                        min={1}
                        max={MAX_ZOOM}
                        step={0.1}
                        value={zoom}
                        onChange={handleZoomChange}
                        style={{width: 150}}
                        color="secondary"
                     />
                     <IconButton
                        onClick={() => updateZoom(Math.min(zoomRef.current * 1.2, MAX_ZOOM))}
                     >
                        <ZoomIn />
                     </IconButton>
                  </div>
                  <ThemeToggler />
                  <div>
                     <Divider orientation="vertical" flexItem />
                     <IconButton
                        onClick={handleDownloadSvg}
                        sx={{marginRight: '2rem'}}
                        title={t('Download SVG Chart')}
                     >
                        <Download />
                     </IconButton>
                     <IconButton onClick={() => window.print()} title={t('Print page')}>
                        <Print />
                     </IconButton>
                  </div>
               </div>
            </Toolbar>
         </MuiAppBar>
         <Toolbar ref={toolbarRef} />
         <div
            ref={chartRef}
            onMouseMove={handleMouseMoveForTooltip}
            style={{
               position: 'relative',
               marginTop: 10, // border bottom of header
               width: canvasWidth,
               height: canvasHeight,
               overflow: 'hidden',
            }}
         >
            {/* Chart container with fixed dimensions */}
            <Box
               ref={svgContainerRef}
               onWheel={handleWheel}
               onMouseDown={handleMouseDown}
               sx={(theme) => ({
                  position: 'absolute',
                  left: '0px',
                  top: '0px',
                  width: canvasWidth,
                  height: canvasHeight,
                  transform: `scale(${zoomRef.current})`,
                  transformOrigin: 'top left',
                  cursor: 'grab',
                  '& .google-visualization-tooltip': {
                     // transform: `scale(${1 / zoom})`,
                     display: 'none',
                  },
                  '& text': {
                     fill: theme.palette.text.primary,
                     fontSize: `${baseOptions.sankey.node.label.fontSize / zoom}px !important`,
                  },
               })}
            >
               {data && data.length > 1 ? (
                  <Chart
                     chartType="Sankey"
                     width={`${canvasWidth}px`}
                     height={`${canvasHeight}px`}
                     data={data}
                     options={baseOptions}
                     chartEvents={chartEvents}
                  />
               ) : (
                  <Typography></Typography>
               )}
            </Box>
            {/* Custom Tooltip element */}
            <Box
               ref={tooltipRef}
               sx={{
                  position: 'absolute',
                  pointerEvents: 'none',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  display: 'none',
                  zIndex: 100,
               }}
            />
         </div>
         <FilterTransactionDialog
            header={t('Generate Connection Map')}
            open={open}
            initialValues={filterData}
            onClose={(data?: IFilterTransactions) => {
               if (data) {
                  fetchData(data);
                  setFilterData(data);
               }
               handleClose();
            }}
         />
      </div>
   );
};

export default SankeyChart;
